import React, { useState, useEffect } from 'react';
import {
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalFooter,
    ModalBody,
    ModalCloseButton,
    Button,
    VStack,
    HStack,
    Text,
    Input,
    Divider,
    Box,
    Spinner,
    Alert,
    AlertIcon,
    useToast
} from 'components-lib';
import { axiosInstance } from 'config/api';
// import { saveToStorage, STORAGE_KEYS } from '../../components/utils/storageUtils';

/*
  LoadTemplateModal: primary component that fetches a saved template from the server,
  normalizes it into the app shapes (medicines / advices / complaints) and *notifies the
  parent* by calling the `setTemplateData(...)` prop.

  Design note: the canonical documentation for the flow is in
  `docs/LOAD_TEMPLATE_FLOW.md`. The modal provides two ways to apply templates:
  1) Notify parent via `setTemplateData(...)` (current default). The parent stores the
    template as the single source-of-truth and child components react to changes.
  2) Directly call local setters via `applyTemplateData(...)` (helper available in this file)
    — use this only if you want the modal to mutate child state directly. Avoid calling both.

  See docs/LOAD_TEMPLATE_FLOW.md for full flow and file pointers.
*/

const LOCAL_STORAGE_KEY = 'doctor_templates';

const LoadTemplateModal = ({
    setTemplateData,
    isOpen,
    onClose,
    onTemplateApplied,
    doctor_id

}) => {
    const [templates, setTemplates] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const toast = useToast();

    // Fetch templates from server and store in localStorage
    useEffect(() => {
        if (isOpen) {
            fetchTemplates();
        }
        // eslint-disable-next-line
    }, [isOpen, searchQuery]);

    /**
     * fetchTemplates
     * ----------------
     * Purpose:
     * - Retrieve the list of templates to show in the modal. Supports optional search.
     * - Cache the latest list to localStorage so the UI can fall back when offline.
     *
     * Key behaviors:
     * - GET `api/templates/getAll` or with `?template_name=` when `searchQuery` is provided.
     * - On success: update local `templates` state and persist to localStorage.
     * - On failure: read from localStorage (if present) and notify the user via toast.
     *
     * Why this is separate:
     * - The UI needs only a lightweight list for selection, so we fetch a small list here.
     * - Fetching the full template payload is deferred until a specific template is chosen
     *   (`fetchTemplateData`) to reduce bandwidth and latency.
     */
    const fetchTemplates = async () => {
        setIsLoading(true);
        try {
            let url;
            url = 'api/templates/getAll';
            if (doctor_id) {
                url = 'api/templates/byDoctor';
                url += `?doctor_id=${doctor_id}`;
            }

            const response = await axiosInstance.get(url);
            if (response.data && Array.isArray(response.data.templates)) {
                setTemplates(response.data.templates);
                localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(response.data.templates));
            } else {
                setTemplates([]);
                localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify([]));
            }
        } catch (error) {
            // fallback to localStorage if available
            const localTemplates = localStorage.getItem(LOCAL_STORAGE_KEY);
            if (localTemplates) {
                setTemplates(JSON.parse(localTemplates));
            } else {
                setTemplates([]);
            }
            toast({
                title: "Error",
                description: "Failed to fetch templates from server. Showing local templates if available.",
                status: "error",
                duration: 3000,
                isClosable: true,
            });
        } finally {
            setIsLoading(false);
        }
    };

    // Separate: fetch -> transform -> apply
    /**
     * fetchTemplateData(tplId)
     * -------------------------
     * Purpose:
     * - Fetch the full payload (medicines, advice, complaints, etc.) for a single template.
     * - Returns `response.data` or an empty object.
     *
     * Notes:
     * - This is intentionally a separate network call from the templates list to keep
     *   the initial listing fast and to fetch the full details only when needed.
     */
    const fetchTemplateData = async (tplId) => {
        const url = `/api/templates/loadData?template_id=${encodeURIComponent(tplId)}`;
        const response = await axiosInstance.get(url);
        return response.data || {};
    };

    /**
     * transformTemplateData(respData)
     * --------------------------------
     * Purpose:
     * - Convert the server shape into the frontend's normalized shape so child components
     *   can consume it consistently.
     *
     * Transform rules (examples):
     * - medicines: map server keys (medicine_no, when_to_take, from_date, to_date) =>
     *     { no, name, type, dosage, frequency, duration, howToTake, fromTo }
     * - advice: map advice_text => advice and preserve id/translations if present
     * - complaints: map complaint_no, complaint, severity, duration, date => normalized fields
     *
     * Return:
     * - { transformedMedicines, transformedAdvices, transformedComplaints, adviceFromServer, complaintsFromServer }
     */
    const transformTemplateData = (respData) => {
        // Support both old and new API shapes
        const data = respData.data || respData || {};

        const medicinesFromServer = data.medicines || [];
        const adviceFromServer = data.advice || [];
        const complaintsFromServer = data.complaints || [];

        const transformedMedicines = medicinesFromServer.map((m) => ({
            no: m.medicine_no,
            name: m.name,
            type: m.type,
            dosage: m.dosage,
            frequency: m.frequency,
            duration: m.duration,
            howToTake: m.when_to_take,
            fromTo: `${m.from_date} - ${m.to_date}`,
        }));

        const transformedAdvices = adviceFromServer.map((a) => ({
            advice: a.advice_text,
            id: a.id,
            date: a.date,
            translations: a.translations || [],
        }));

        const transformedComplaints = complaintsFromServer.map((c) => ({
            no: c.complaint_no,
            complaint: c.complaint,
            severity: c.severity,
            duration: c.duration,
            date: c.date,
        }));

        // New fields
        const next_visit = data.template?.next_visit || '';
        const diagnosis = data.diagnosis || [];
        const clinical_findings = data.clinical_findings || [];
        const referred_to = data.referred_to || [];
        const investigation = data.template?.investigation || '';
        const past_medication = data.template?.past_medication || '';

        return {
            transformedMedicines,
            transformedAdvices,
            transformedComplaints,
            adviceFromServer,
            complaintsFromServer,
            next_visit,
            diagnosis,
            clinical_findings,
            referred_to,
            investigation,
            past_medication,
        };
    };



    // Transform server template to internal format and apply
    const handleSelectTemplate = async (template) => {
        /**
         * handleSelectTemplate
         * ---------------------
         * Full flow when user picks a template from the list:
         * 1) Resolve template id from possible fields. If missing, show error.
         * 2) Set loading state and call `fetchTemplateData(tplId)` to retrieve the full payload.
         * 3) Call `transformTemplateData(respData)` to normalize server fields to frontend shapes.
         * 4) Notify the parent by calling `setTemplateData({ medicines, advices, complaints })`.
         *    - This is the repo's canonical approach: the parent (`PatientDetails`) becomes the
         *      single source of truth for the template payload.
         *    - Child components receive `templateData` via props and each component's `useEffect`
         *      will parse/merge/dedupe into local patient state using the setters from `usePatientData`.
         *
         * Alternative:
         * - There is a helper-style approach (implemented elsewhere in this file in older code)
         *   that can `applyTemplateData(...)` by calling child setters directly (e.g. `setMedicinesData`).
         * - Avoid calling both `setTemplateData` and `applyTemplateData` to prevent double-apply.
         */
        const tplId = template?.template_id || template?.templateId || template?.id;
        if (!tplId) {
            toast({
                title: "Template ID missing",
                description: "Cannot load template: missing template id",
                status: "error",
                duration: 3000,
                isClosable: true,
            });
            return;
        }

        setIsLoading(true);
        try {
            const respData = await fetchTemplateData(tplId);

            console.log("🚀 Fetched Template Data:", respData);
            // alert(`Fetched Template Data: ${JSON.stringify(respData)}`);

            const {
                transformedMedicines,
                transformedAdvices,
                transformedComplaints,
                // adviceFromServer,
                // complaintsFromServer,
                next_visit,
                diagnosis,
                clinical_findings,
                referred_to,
                investigation,
                past_medication,
            } = transformTemplateData(respData);

            console.log("📝 Transformed Medicines:", transformedMedicines);
            console.log("📝 Transformed Advices:", transformedAdvices);
            console.log("📝 Transformed Complaints:", transformedComplaints);


            // Notify parent that template has been applied so children can resync
            try {
                if (typeof setTemplateData === 'function') {
                    setTemplateData({
                        medicines: transformedMedicines,
                        advices: transformedAdvices,
                        complaints: transformedComplaints,
                        next_visit,
                        diagnosis,
                        clinical_findings,
                        referred_to,
                        investigation,
                        past_medication,
                    });
                }
            } catch (e) {
                console.warn('onTemplateApplied callback failed:', e);
            }

            toast({
                title: "Template Loaded",
                description: `Template "${template.template_name}" loaded successfully!`,
                status: "success",
                duration: 3000,
                isClosable: true,
            });

            onClose();
        } catch (error) {
            console.error("❌ Error loading template:", error);
            toast({
                title: "Error",
                description: "Failed to load template data. See console for details.",
                status: "error",
                duration: 4000,
                isClosable: true,
            });
        } finally {
            setIsLoading(false);
        }
    };


    // client-side filtered list for immediate UI responsiveness
    const filteredTemplates = React.useMemo(() => {
        const q = searchQuery.trim().toLowerCase();
        if (!q) return templates;
        return templates.filter(t => {
            const name = (t.template_name || '').toLowerCase();
            const desc = (t.template_description || '').toLowerCase();
            return name.includes(q) || desc.includes(q);
        });
    }, [templates, searchQuery]);


    return (
        <Modal isOpen={isOpen} onClose={onClose} size="lg" isCentered>
            <ModalOverlay />
            <ModalContent h="80vh" maxH="90vh" w="max-content" minW="xl" maxW="90vw" mx="auto" px={4}>
                <ModalHeader>Load Template</ModalHeader>
                <ModalCloseButton />
                {/* Allow body to scroll while header/footer remain fixed */}
                <ModalBody overflowY="auto" px={4} py={3}>
                    <VStack spacing={4} align="stretch">
                        <Box>
                            <Text fontWeight="bold" mb={2}>Search Templates:</Text>
                            <Input
                                placeholder="Enter template name to search..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                aria-label="Search templates"
                            />
                        </Box>
                        <Text fontWeight="bold" mb={4}>Available Templates:</Text>

                        {/*<Divider />*/}
                        {isLoading ? (
                            <Box textAlign="center" py={6}>
                                <Spinner size="lg" color="blue.500" />
                                <Text mt={2}>Loading templates...</Text>
                            </Box>
                        ) : (
                            <Box>

                                {filteredTemplates.length > 0 ? (
                                    // Grid layout for templates: responsive up to 4 columns
                                    <Box
                                        display="grid"
                                        /* Keep responsive behavior but set a fixed column width for 4-col layout */
                                        gridTemplateColumns={{
                                            base: '1fr',
                                            sm: 'repeat(2, 1fr)',
                                            md: 'repeat(3, 1fr)',
                                            /* when 4 columns, use fixed width columns so each card has consistent width */
                                            lg: 'repeat(4, 260px)'
                                        }}
                                        gap={3}
                                        justifyContent="center" /* center the grid when using fixed column widths */
                                    >
                                        {filteredTemplates.map((template) => (
                                            <Box
                                                key={template._id || template.id}
                                                borderWidth="1px"
                                                borderColor={"gray.400"}
                                                borderRadius="md"
                                                p={3}
                                                bg="gray.50"
                                                _hover={{ boxShadow: "md" }}
                                                /* ensure fixed-width cards don't stretch */
                                                width={{ base: '100%', lg: '260px' }}
                                            >
                                                <HStack justify="space-between" align="flex-start">
                                                    <Box mr={3} flex="1">
                                                        <Text fontWeight="semibold">{template.template_name}</Text>
                                                        <Text fontSize="sm" color="gray.600" noOfLines={3}>
                                                            {template.template_description}
                                                        </Text>
                                                    </Box>
                                                    <Button
                                                        colorScheme="blue"
                                                        size="sm"
                                                        onClick={() => handleSelectTemplate(template)}
                                                        aria-label={`Use template ${template.template_name}`}
                                                    >
                                                        Use
                                                    </Button>
                                                </HStack>
                                            </Box>
                                        ))}
                                    </Box>
                                ) : (
                                    <Alert status="info">
                                        <AlertIcon />
                                        {searchQuery ? 'No templates found matching your search.' : 'No templates available.'}
                                    </Alert>
                                )}
                            </Box>
                        )}
                    </VStack>
                </ModalBody>
                <ModalFooter>
                    <Button variant="ghost" mr={3} onClick={onClose}>
                        Cancel
                    </Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
};

export default LoadTemplateModal;

